import React from 'react';
import { Route, Navigate, useParams, useLocation, useOutletContext } from "react-router-dom";
import { peekDemoBypass, DemoBypassGuard, isAdminRole } from './helpers';
import ProtectedRoute from '../../components/ProtectedRoute';
import ErrorBoundary from '../../components/ErrorBoundary';
import ErrorFallback from '../../components/shared/ErrorFallback';

/**
 * Redirect component for legacy folder routes to /library/:folderId
 * Preserves the folderId parameter in the redirect.
 */
function LegacyFolderRedirect() {
  const { folderId } = useParams();
  return <Navigate to={`/library/${folderId}`} replace />;
}

/**
 * Redirect component for legacy /work-products/write/:typeId to /projects/canvas/:typeId
 * Preserves the typeId parameter in the redirect.
 */
function LegacyWriteRedirect() {
  const { typeId } = useParams();
  return <Navigate to={`/projects/canvas/${typeId}`} replace />;
}

/**
 * Redirect component for /patients/:id to /clients/:id
 * Preserves any subpaths (e.g., /patients/123/notes -> /clients/123/notes)
 */
function PatientToClientRedirect() {
  const { id, '*': rest } = useParams();
  const location = useLocation();
  const subpath = rest ? `/${rest}` : '';
  return (
    <Navigate
      to={{
        pathname: `/clients/${id}${subpath}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

/**
 * Redirect component for legacy /clients/:id/appointments-billing/billing to /clients/:id/billing.
 */
function ClientBillingRedirect() {
  const { id } = useParams();
  return <Navigate to={`/clients/${id}/billing`} replace />;
}

function CaseInfoRoute() {
  const { caseData } = useOutletContext<{ caseData?: { case_type?: string } }>();
  return caseData?.case_type === 'individual' ? (
    <ClientInfoTab />
  ) : (
    <Navigate to="../people" replace />
  );
}

import {
  NotePage, NoteDashboard, NewNotePageEnhanced, NoteTemplates,
  ClientDetailPage, ClientInfoTab, NotesTab, PersonCaseEntryPage, ClientRecordsPage,
  AppointmentsBillingTab, PersonCasesEncountersTab, ScheduleTab, BillingTab, BillingSettingsTab,
  SuperbillsTab, PatientHealthDataTab, HealthTrackingTab, ScreeningTab,
  TreatmentPlanTab, MedicationsTab, AllergiesTab,
  ClinicalHistoryTab, MessagesTab,
  ConsentsDocumentsTab, OutcomesTab, LabResultsTab, AssessmentsTab, NotificationsTab,
  VaultDashboard, TherapistReflectionsPage, NewDashboardPage, NewTabPage,
  LeadAnalytics, TodoListPage, TaskAutomationPage,
  CalendarPage, MessagesPage,
  MySettingsPage, MyWorkPage, TherapistHealthTracking, PatientManagement,
  MyOutcomesPage, DriveModePage,
  UnattributedEncountersPage, CaseCreatePage, CaseDetailPage,
  CaseOverviewTab, CaseIntakeReferralTab, CaseEncountersTab, CasePeopleTab, CaseNotesTab, CaseAwarePaperworkTab, CaseBillingTab, CaseBillingSettingsTab,
  CaseNeedsAttentionTab,
  CaseAssessmentsTab,
  CaseHealthDataTab,
  CaseMessagesTab,
  WorkProductTypesPage, WritingWorkspace, QuickHandoutPage, ProviderGettingStartedPage,
} from './therapistPageImports';

import TherapistPanel from '../../features/therapist-panel/components/TherapistPanel';
import TherapistChatLayout from '../../components/layouts/TherapistChatLayout';

/**
 * Therapist Routes (Part 1) - Core Provider Routes
 * Dashboard, AI Assistant, Notes, Clients, Projects, Library, Tasks
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function TherapistRoutes({ user, userHasRole, isClientUser = (_user: unknown) => true }) {
  return (
    <>
              {/* Legacy /therapist-panel is fully cut — redirect all subpaths to /my-settings */}
              <Route path="/therapist-panel" element={<Navigate to="/my-settings" replace />} />
              <Route path="/therapist-panel/*" element={<Navigate to="/my-settings" replace />} />

              {/* AI Assistant (both paths) */}
              {['/assistant', '/ai-assistant'].map((path) => (
                <Route key={path} path={path} element={
                  user === null ? <div>Checking login...</div>
                    : user ? isClientUser(user) ? <Navigate to="/client" /> : <TherapistChatLayout><TherapistPanel user={user} /></TherapistChatLayout>
                    : <Navigate to="/login" />
                } />
              ))}

              {/* Dashboard Page */}
              <Route
                path="/dashboard"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : (user && peekDemoBypass('/dashboard', ['therapist','student_therapist','supervisor','psychiatric_np']))
                    ? <DemoBypassGuard><ErrorBoundary fallback={<ErrorFallback title="Dashboard" />}><NewDashboardPage /></ErrorBoundary></DemoBypassGuard>
                    : !user
                    ? <DemoBypassGuard><Navigate to="/login" /></DemoBypassGuard>
                    : userHasRole(user, 'platform_owner')
                    ? <Navigate to="/platform-owner" replace />
                    : isClientUser(user)
                    ? <Navigate to="/client" />
                    : isAdminRole(user)
                    ? <Navigate to="/admin/dashboard" replace />
                    : <ErrorBoundary fallback={<ErrorFallback title="Dashboard" />}><NewDashboardPage /></ErrorBoundary>
                }
              />

              <Route path="/dashboard-old" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard-new" element={<Navigate to="/dashboard" replace />} />

              {/* New Tab Page - accessible to all authenticated users (role filtering handled by NewTabPage) */}
              <Route
                path="/new-tab"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? <NewTabPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Notes Dashboard */}
              <Route
                path="/notes"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <NoteDashboard />
                    : <Navigate to="/login" />
                }
              />

              {/* New Note Compose — requires clinical note creation permission */}
              <Route
                path="/notes/new"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <ProtectedRoute requiresPermission="notes.create"><NewNotePageEnhanced /></ProtectedRoute>
                    : <Navigate to="/login" />
                }
              />

              {/* Single Note View */}
              <Route
                path="/notes/:id"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <NotePage />
                    : <Navigate to="/login" />
                }
              />

              {/* Note Templates */}
              <Route
                path="/templates"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <NoteTemplates />
                    : <Navigate to="/login" />
                }
              />

              {/* Redirect legacy patient list to the canonical case caseload. */}
              <Route path="/patients" element={<Navigate to="/cases" replace />} />
              <Route path="/patients/:id" element={<PatientToClientRedirect />} />
              <Route path="/patients/:id/*" element={<PatientToClientRedirect />} />

              {/* Legacy clients list URL — the unified case caseload is canonical. */}
              <Route
                path="/clients"
                element={
                  <Navigate to="/cases" replace />
                }
              />

              {/* Organization-wide Person roster, including people without Cases. */}
              <Route
                path="/client-records"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <ClientRecordsPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Unified case-driven caseload */}
              <Route
                path="/cases"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <PatientManagement />
                    : <Navigate to="/login" />
                }
              />

              <Route
                path="/encounters/unattributed"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <UnattributedEncountersPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Create New Case */}
              <Route
                path="/cases/new"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <CaseCreatePage />
                    : <Navigate to="/login" />
                }
              />

              {/* Unified Case Workspace — same shell for individual + group cases */}
              <Route
                path="/cases/:caseId"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <CaseDetailPage />
                    : <Navigate to="/login" />
                }
              >
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<CaseOverviewTab />} />
                <Route path="intake-referral" element={<CaseIntakeReferralTab />} />
                <Route path="needs-attention" element={<CaseNeedsAttentionTab />} />
                {/* Personal Information is only defined for an Individual Case. */}
                <Route path="info" element={<CaseInfoRoute />} />
                <Route path="encounters" element={<CaseEncountersTab />} />
                <Route path="notes" element={<CaseNotesTab />} />
                <Route path="billing" element={<CaseBillingTab />} />
                <Route path="billing-settings" element={<CaseBillingSettingsTab />} />
                <Route path="people" element={<CasePeopleTab />} />
                <Route path="members" element={<Navigate to="../people" replace />} />
                {/* Legacy providers route now merges into People */}
                <Route path="providers" element={<Navigate to="../people" replace />} />
                <Route path="people/:patientId/info" element={<ClientInfoTab />} />
                <Route path="medications" element={<MedicationsTab />} />
                <Route path="health-data" element={<CaseHealthDataTab />}>
                  <Route index element={null} />
                  <Route path="overview" element={null} />
                  <Route path="health-tracking" element={<HealthTrackingTab />} />
                  <Route path="screening" element={<ScreeningTab />} />
                  <Route path="outcomes" element={<OutcomesTab />} />
                  <Route path="treatment-plan" element={<TreatmentPlanTab />} />
                  <Route path="medications" element={<MedicationsTab />} />
                  <Route path="allergies" element={<AllergiesTab />} />
                  <Route path="problem-list" element={<Navigate to="../treatment-plan" replace />} />
                  <Route path="clinical-history" element={<ClinicalHistoryTab />} />
                  <Route path="lab-results" element={<LabResultsTab />} />
                </Route>
                <Route path="assessments" element={<CaseAssessmentsTab />}>
                  <Route index element={<AssessmentsTab />} />
                </Route>
                <Route path="documents" element={<CaseAwarePaperworkTab />} />
                <Route path="paperwork" element={<Navigate to="../documents" replace />} />
                <Route path="consents-documents" element={<Navigate to="../documents" replace />} />
                <Route path="consents" element={<Navigate to="../documents" replace />} />
                <Route path="messages" element={<CaseMessagesTab />} />
                {/* Notifications hidden until a Case-scoped surface lands */}
              </Route>

              {/* Single Client Detail View with nested tabs */}
              <Route
                path="/clients/:id"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <ClientDetailPage />
                    : <Navigate to="/login" />
                }
              >
                <Route index element={<PersonCaseEntryPage />} />
                <Route path="info" element={<ClientInfoTab />} />
                {/* Notes removed from Person navigation; route preserved for direct links */}
                <Route path="notes" element={<NotesTab />} />
                <Route path="cases-encounters" element={<PersonCasesEncountersTab />} />
                <Route path="encounters" element={<Navigate to="../cases-encounters" replace />} />
                <Route path="appointments-billing" element={<AppointmentsBillingTab />}>
                  <Route index element={<Navigate to="schedule" replace />} />
                  <Route path="schedule" element={<ScheduleTab />} />
                  <Route path="billing" element={<ClientBillingRedirect />} />
                  <Route path="billing-settings" element={<BillingSettingsTab />} />
                  <Route path="superbills" element={<SuperbillsTab />} />
                </Route>
                <Route path="billing" element={<BillingTab />} />
                <Route path="medications" element={<MedicationsTab />} />
                <Route path="documents" element={<ConsentsDocumentsTab />} />
                <Route path="consents-documents" element={<Navigate to="../documents" replace />} />
                <Route path="consents" element={<Navigate to="../documents" replace />} />
                <Route path="health-data" element={<PatientHealthDataTab />}>
                  <Route index element={null} />
                  <Route path="overview" element={null} />
                  <Route path="health-tracking" element={<HealthTrackingTab />} />
                  <Route path="screening" element={<ScreeningTab />} />
                  <Route path="outcomes" element={<OutcomesTab />} />
                  <Route path="treatment-plan" element={<TreatmentPlanTab />} />
                  <Route path="medications" element={<MedicationsTab />} />
                  <Route path="allergies" element={<AllergiesTab />} />
                  <Route path="problem-list" element={<Navigate to="../treatment-plan" replace />} />
                  <Route path="clinical-history" element={<ClinicalHistoryTab />} />
                  <Route path="lab-results" element={<LabResultsTab />} />
                </Route>
                <Route path="assessments" element={<AssessmentsTab />} />
                <Route path="messages" element={<MessagesTab />} />
                <Route path="notifications" element={<NotificationsTab />} />
              </Route>

              {/* Lead Analytics */}
              <Route
                path="/leads/analytics"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <LeadAnalytics />
                    : <Navigate to="/login" />
                }
              />

              {/* Tasks / To-Do List */}
              <Route
                path="/tasks"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="To-Do list" />}>
                        <TodoListPage user={user} />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              <Route path="/todo" element={<Navigate to="/tasks" replace />} />
              <Route path="/todo-list" element={<Navigate to="/tasks" replace />} />
              <Route path="/todos" element={<Navigate to="/tasks" replace />} />

              {/* Task Automation */}
              <Route
                path="/automation"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (userHasRole(user, 'intake_coordinator') || isAdminRole(user) || !user.roles || user.roles.length === 0) ? <TaskAutomationPage /> : <Navigate to="/dashboard" />
                    : <Navigate to="/login" />
                }
              />

              <Route
                path="/admin/task-automation"
                element={<Navigate to="/admin/settings?tab=communications" replace />}
              />

              {/* Therapist Reflections */}
              <Route
                path="/reflections"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <TherapistReflectionsPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Projects — AI-assisted and manual document creation (Canvas) */}
              <Route
                path="/projects"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Projects" />}>
                        <WorkProductTypesPage />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              {/* Canvas — the AI writing workspace within Projects */}
              <Route
                path="/projects/canvas/:typeId"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Canvas" />}>
                        <WritingWorkspace />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              <Route
                path="/projects/quick-handout"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Quick Handout" />}>
                        <QuickHandoutPage />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              {/* Library — document storage, organization, and client sharing */}
              <Route
                path="/library"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Library" />}>
                        <VaultDashboard />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              <Route
                path="/library/:folderId"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Library" />}>
                        <VaultDashboard />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              {/* Legacy aliases → new routes */}
              <Route path="/work-products" element={<Navigate to="/projects" replace />} />
              <Route path="/work-products/write/:typeId" element={<LegacyWriteRedirect />} />
              <Route path="/work-products/:folderId" element={<LegacyFolderRedirect />} />
              <Route path="/vault" element={<Navigate to="/library" replace />} />
              <Route path="/vault/:folderId" element={<LegacyFolderRedirect />} />
              <Route path="/practice-hub" element={<Navigate to="/library" replace />} />
              <Route path="/practice-hub/:folderId" element={<LegacyFolderRedirect />} />

              {/* Getting Started — role-aware onboarding hub */}
              <Route
                path="/getting-started"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user)
                      ? <Navigate to="/client/getting-started" replace />
                      : (
                        <ErrorBoundary fallback={<ErrorFallback title="Getting Started" />}>
                          <ProviderGettingStartedPage isAdmin={isAdminRole(user)} />
                        </ErrorBoundary>
                      )
                    : <Navigate to="/login" />
                }
              />


              {/* Calendar Page */}
              <Route
                path="/calendar"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : (
                      <ErrorBoundary fallback={<ErrorFallback title="Calendar" />}>
                        <CalendarPage />
                      </ErrorBoundary>
                    )
                    : <Navigate to="/login" />
                }
              />

              {/* My Settings Page - Unified settings with tabs */}
              <Route
                path="/my-settings"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? <MySettingsPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Legacy Settings Routes - Redirect to new unified page */}
              {['/settings', '/settings/work-schedule', '/profile'].map((p) => (
                <Route key={p} path={p} element={<Navigate to="/my-settings" replace />} />
              ))}

              {/* My Work Page - Unified timesheet, payroll, analytics */}
              <Route
                path="/my-work"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? <MyWorkPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Legacy routes - Redirect to new unified My Work page */}
              <Route path="/timesheets" element={<Navigate to="/my-work" replace />} />
              <Route path="/my-payroll" element={<Navigate to="/my-work" replace />} />

              {/* Therapist Health Tracking Review */}
              <Route
                path="/therapist/health-tracking"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <TherapistHealthTracking />
                    : <Navigate to="/login" />
                }
              />

              {/* My Outcomes Dashboard */}
              <Route
                path="/outcomes"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client" /> : <MyOutcomesPage />
                    : <Navigate to="/login" />
                }
              />

              {/* Legacy hands-free notes redirects to Drive Mode */}
              <Route path="/hands-free-notes" element={<Navigate to="/drive-mode" replace />} />

              {/* Drive Mode - Hands-free note writing while driving */}
              <Route
                path="/drive-mode"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user)
                      ? <Navigate to="/client" />
                      : <ProtectedRoute requiresPermission="notes.create"><DriveModePage /></ProtectedRoute>
                    : <Navigate to="/login" />
                }
              />

              {/* Legacy Voice Mode redirects → Drive Mode */}
              <Route path="/voice-mode" element={<Navigate to="/drive-mode" replace />} />

              {/* The clinician bell is rendered by the shared layout. */}
              <Route
                path="/notifications"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? <Navigate to="/dashboard" replace />
                    : <Navigate to="/login" />
                }
              />

              {/* Messages Page - Provider/staff messaging (clients use /client/messages) */}
              <Route
                path="/messages"
                element={
                  user === null
                    ? <div>Checking login...</div>
                    : user
                    ? isClientUser(user) ? <Navigate to="/client?tab=messages" /> : <MessagesPage />
                    : <Navigate to="/login" />
                }
              />

              <Route path="/e-prescribe" element={<Navigate to="/my-settings?tab=profile" replace />} />

              {/* Documents Page — redirected to Library (consolidated) */}
              <Route
                path="/documents"
                element={<Navigate to="/library" replace />
                }
              />

    </>
  );
}
